import { css, cx } from "@emotion/css";
import { CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextPrimaryButton } from "src/components/Default/LekUI/Button";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { EllipsisText } from "src/components/Main/News/EllipsisText";
import { ForumCommentContent } from "src/components/Main/News/Forum/ForumCommentContent";
import Gallery from "src/components/NewComponents/Images/Gallery";
import isMobile from "src/helpers/isMobile";
import { useGetForumCommentSubcomments } from "src/hooks/useComments";
import { useFontSize } from "src/hooks/useFontSize";
import ArrowDownIcon from "src/images/Icons/newIcons/arrowDown.svg";
import ArrowUpIcon from "src/images/Icons/newIcons/arrowUp.svg";
import AdminProfileIcon from "src/images/Icons/newIcons/userProfile/logo.svg";
import ProfileIcon from "src/images/Icons/newIcons/userProfile/profile.svg";

interface Comment {
	id: string;
	comment: string;
	date: string;
	subcommentsCount: number;
	images?: { url: string }[];
	user: {
		id: string;
		username: string;
		isStaff: boolean;
	};
	subComments?: Comment[];
}

interface BasicUserQuestionProps {
	comment: Comment;
	forumComments: Comment | null;
	onOpen: (isOpen: boolean) => void;
	isOpen: boolean;
	className?: string;
}

const BasicUserQuestion = ({
	comment,
	forumComments,
	onOpen,
	isOpen,
	className,
}: BasicUserQuestionProps) => {
	const { fontSizeClass } = useFontSize();
	const Logo = comment.user.isStaff ? AdminProfileIcon : ProfileIcon;
	const { t } = useTranslation();
	const Icon = isOpen ? ArrowUpIcon : ArrowDownIcon;

	const renderSeeMore = () => {
		const count = forumComments?.subComments
			? forumComments?.subComments.length
			: comment.subcommentsCount;

		if (count === 0) {
			return "Udziel odpowiedzi na pytanie";
		}

		return `Zobacz odpowiedzi (${count})`;
	};

	return (
		<div className={cx(className, "content-box flex-col last_comment")}>
			<div className="flex gap-16">
				<Tooltip
					title={comment.user.isStaff ? t("tooltips.admin") : ""}
					className="h-fit-content"
				>
					<div className="profile--user-avatar square-48">
						<Logo className="icon-normal" />
					</div>
				</Tooltip>
				<div className="flex-1">
					<p className={`m-0 ${fontSizeClass}`}>
						<b>{comment.user.username}</b> {t("news.forum.userComment")}{" "}
					</p>
					<span className="text-grey">
						{dayjs(comment.date).format("DD MMM, HH:mm")}
					</span>
				</div>
			</div>
			<div className="flex mt-12 gap-16 flex-col">
				<div className="flex-1 flex-col gap-12 flex">
					<EllipsisText
						fontSizeClassName={fontSizeClass}
						className={`${fontSizeClass} fr-view`}
						html={comment.comment}
					/>
					{comment?.images?.[0]?.url && (
						<div>
							<Gallery
								images={[{ image: comment.images[0].url }]}
								maxImages={isMobile() ? 5 : null}
								onDelete={undefined}
							/>
						</div>
					)}
				</div>
				<div>
					<TextPrimaryButton
						onClick={() => onOpen(!isOpen)}
						className={cx(
							"p-2",
							css`
                  height: unset;
              `,
						)}
						endIcon={<Icon className="icon-normal" />}
					>
						{renderSeeMore()}
					</TextPrimaryButton>
				</div>
				{isOpen && forumComments && (
					<div>
						<ForumCommentContent
							comments={forumComments.subComments}
							isActive
							parentComment={comment}
							placeholder={t("exam.comments.replyComment")}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

interface UserQuestionProps {
	comment: Comment;
}

export const UserQuestion = ({ comment }: UserQuestionProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { data: forumComments } = useGetForumCommentSubcomments(
		comment.id,
		isOpen,
	);

	return (
		<BasicUserQuestion
			comment={comment}
			forumComments={forumComments}
			onOpen={setIsOpen}
			isOpen={isOpen}
		/>
	);
};

interface NotificationUserQuestionProps {
	parentId: string;
}

export const NotificationUserQuestion = ({ parentId }: NotificationUserQuestionProps) => {
	const [isOpen, setIsOpen] = useState(true);
	const { data: forumComments } = useGetForumCommentSubcomments(
		parentId,
		isOpen,
	);

	if (!forumComments) {
		return (
			<div className="flex-center-row">
				<CircularProgress
					className={css`
              width: 80px;
              height: 80px;
              color: var(--black80);
          `}
				/>
			</div>
		);
	}

	return (
		<BasicUserQuestion
			comment={forumComments}
			forumComments={forumComments}
			onOpen={setIsOpen}
			isOpen={isOpen}
			className="border-none"
		/>
	);
};
